/**
 * SA-01 фаза 1 — редактируемый бейдж статуса посева.
 *
 * До сих пор статус можно было поправить только «попутно», вместе с культурой
 * и урожайностью, и нигде не было видно, кто его менял. Здесь: бейдж → меню
 * статусов → дата события → необязательный комментарий → под бейджем подпись
 * «Убран · Иванов, 12.09». Автомат от операций (уборка закрывает посев) живёт
 * отдельно; ручная правка нужна там, где автомат промахнулся или работ просто
 * нет (история).
 *
 * Права нет (`canWrite=false`) → обычный read-only бейдж, без меню.
 *
 * AK-513 (находка head-web на ревью PR #612, ADR-059 §D7 «Хранимое»): до этой
 * правки меню предлагало 8 легаси-кодов (`sown`/`growing`/`partial_harvest`/
 * `harvested`/`reseeded`/`failed`/`closed`/`planned`). После свёртки словаря
 * подписей AK-509 (`lib/sowings/stage.ts` SOWING_STAGE_LABELS) четыре из
 * восьми — `sown`/`growing`/`partial_harvest`/`reseeded` — стали печатать
 * ОДНО слово «Растёт»: агроном открывал меню и не понимал, какой из четырёх
 * «Растёт» выбирает. Ручная отметка принимает ТОЛЬКО пользовательский канон
 * (ADR-059 §D4/§D7): Запланировано · Растёт · Убрано · Списано.
 * `sown`/`partial_harvest`/`reseeded` из меню убраны — это ВЫЧИСЛЯЕМЫЕ
 * уточнения (§D7 «Вычисляемое»), сервер сворачивает их в «Растёт» сам;
 * `closed`/`finished` собой дублировали «Убрано» и тоже убраны.
 *
 * Растёт/Убрано/Списано — датированный факт (ADR-059 §D4 «рука — событие с
 * датой»): выбор раскрывает обязательное поле даты (по умолчанию — сегодня,
 * можно изменить), комментарий — необязательно; «Сохранить» задизейблена без
 * даты. Паттерн — тот же, что утверждённый wireframe
 * `docs/superpowers/prototypes/field-card-sowings-v2` (состояние 7 «Отметить
 * статус вручную»): один непрерывный список с раскрывающейся датой под
 * выбранным пунктом, общий комментарий, «Отмена»/«Сохранить». «Запланировано»
 * — сброс к плану, событие не датируется.
 *
 * Компонент — единственный потребитель `useSetSowingStatus`; проверено перед
 * правкой (grep по репозиторию), поэтому дата добавлена и в
 * `lib/sowings/statusApi.ts`, и в backend `sowing_status.py` (мост через
 * существующий `field_attribute_history`, без миграции — полноценная
 * событийная модель `sowing_events` из ADR-059 §D4 остаётся отдельным, более
 * крупным эпиком).
 */
import { useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { getSowingStatusInfo } from '@/components/fields/sowingStatus';
import { resolveSowingDisplay } from '@/lib/sowings/seasonDisplay';
import {
  useSetSowingStatus,
  useSowingStatusLog,
  type SowingStatusCode,
} from '@/lib/sowings/statusApi';

interface ManualStatusOption {
  code: SowingStatusCode;
  /** Нужна ли обязательная дата события при выборе (ADR-059 §D4). Плану — нет. */
  needsDate: boolean;
  /** Подпись поля даты — привязана к смыслу события, не к слову статуса. */
  dateLabel: string;
}

/**
 * Ручная отметка — ровно 4 значения канона (ADR-059 §D7), в порядке, как
 * идёт сезон.
 */
const MANUAL_STATUS_ORDER: ManualStatusOption[] = [
  { code: 'planned', needsDate: false, dateLabel: '' },
  { code: 'growing', needsDate: true, dateLabel: 'Дата сева' },
  { code: 'harvested', needsDate: true, dateLabel: 'Дата уборки' },
  { code: 'written_off', needsDate: true, dateLabel: 'Дата акта списания' },
];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

interface Props {
  sowingId: string;
  /** Разрешённый код статуса (ручной либо выведенный из тех.операций). */
  status: string | null | undefined;
  /**
   * Пояснение под бейджем: рука не подтверждена операциями (§D1 п.3) либо пар
   * после сезона (§D5). Показываем ПОМЕТКУ, а не второй статус рядом.
   */
  note?: string | null;
  canWrite?: boolean;
  /**
   * Сезон этого посева завершён (даты рамки прошли). Тогда «Запланировано»
   * читается как «Не реализован» с объяснением — сезон кончился, факт сева
   * никто не зафиксировал. Данные не мутируем: автомат статуса привязан к
   * тех.операциям (ADR-063), правило конвертации истории — отдельно.
   */
  seasonCompleted?: boolean;
  className?: string;
}

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

export function SowingStatusBadge({
  sowingId,
  status,
  note,
  canWrite = false,
  seasonCompleted = false,
  className,
}: Props) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<SowingStatusCode | ''>('');
  const [eventDate, setEventDate] = useState(todayIso);
  const [reason, setReason] = useState('');
  const mutation = useSetSowingStatus(sowingId);
  const log = useSowingStatusLog(sowingId);

  const info = resolveSowingDisplay(status, seasonCompleted);
  const last = log.data?.[0];
  const selectedOption = MANUAL_STATUS_ORDER.find((o) => o.code === selected) ?? null;
  const canSave = Boolean(selected) && (!selectedOption?.needsDate || Boolean(eventDate));

  function resetMenu() {
    setSelected('');
    setEventDate(todayIso());
    setReason('');
  }

  function apply() {
    if (!selectedOption || !canSave) return;
    mutation.mutate(
      {
        status: selectedOption.code,
        reason: reason.trim() || null,
        occurred_on: selectedOption.needsDate ? eventDate : null,
      },
      {
        onSuccess: () => {
          setOpen(false);
          resetMenu();
        },
      },
    );
  }

  const badge = (
    <Badge
      variant="secondary"
      className={cn(info?.className, canWrite && 'cursor-pointer gap-1')}
      title={info?.hint ?? undefined}
    >
      {info?.label ?? 'Без статуса'}
      {canWrite &&
        (mutation.isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5" />
        ))}
    </Badge>
  );

  return (
    <div className={cn('space-y-1', className)}>
      {canWrite ? (
        <DropdownMenu
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) resetMenu();
          }}
        >
          <DropdownMenuTrigger asChild>
            <button type="button" aria-label="Изменить статус посева">
              {badge}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <DropdownMenuLabel>Статус посева</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuRadioGroup
              value={selected}
              onValueChange={(v) => {
                setSelected(v as SowingStatusCode);
                setEventDate(todayIso());
              }}
            >
              {MANUAL_STATUS_ORDER.map((opt) => {
                const label = getSowingStatusInfo(opt.code)?.label ?? opt.code;
                const current = opt.code === status;
                return (
                  <div key={opt.code}>
                    <DropdownMenuRadioItem
                      value={opt.code}
                      onSelect={(e) => e.preventDefault()}
                      disabled={mutation.isPending}
                    >
                      <span className="flex-1">{label}</span>
                      {current && (
                        <span className="text-xs text-muted-foreground">текущий</span>
                      )}
                    </DropdownMenuRadioItem>
                    {selected === opt.code && opt.needsDate && (
                      <div className="space-y-1 px-2 pb-2 pl-8">
                        <Label htmlFor={`event-date-${sowingId}`} className="text-xs">
                          {opt.dateLabel} <span aria-hidden className="text-destructive">*</span>
                        </Label>
                        <Input
                          id={`event-date-${sowingId}`}
                          type="date"
                          value={eventDate}
                          onChange={(e) => setEventDate(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                          className="h-8"
                        />
                        <p className="text-xs text-muted-foreground">
                          по умолчанию — сегодня, можно изменить
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <div className="space-y-2 px-2 pb-2 pt-1">
              <Label htmlFor={`reason-${sowingId}`} className="text-xs">
                Комментарий (необязательно)
              </Label>
              <Input
                id={`reason-${sowingId}`}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Убрали, работу не отметили"
                onKeyDown={(e) => e.stopPropagation()}
              />
            </div>
            <div className="flex justify-end gap-2 px-2 pb-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setOpen(false)}
              >
                Отмена
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={!canSave || mutation.isPending}
                onClick={apply}
              >
                {mutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Сохранить
              </Button>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        badge
      )}

      {/* Пометка вместо второго статуса рядом: пользователь видит ОДИН статус
          и, если нужно, объяснение — откуда он взялся. */}
      {note && <p className="text-xs text-muted-foreground">{note}</p>}

      {/* Подмена показа в завершённом сезоне объясняется прямо на экране, а не
          только тултипом: агроном должен понимать, откуда взялось слово. */}
      {info?.isUnrealized && !note && (
        <p className="text-xs text-muted-foreground">{info.hint}</p>
      )}

      {mutation.isError && (
        <p className="text-xs text-destructive">
          Не удалось изменить статус. Попробуйте ещё раз.
        </p>
      )}

      {last && (
        <p className="text-xs text-muted-foreground">
          {last.status_label}
          {last.who_name ? ` · ${last.who_name}` : ''}
          {last.changed_at ? `, ${fmtWhen(last.changed_at)}` : ''}
          {last.reason ? ` — ${last.reason}` : ''}
        </p>
      )}
    </div>
  );
}
